/**
 * 月子中心订单表格组件
 * 专门处理月子中心订单的数据结构和显示
 */
import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Table, Button, Space } from 'antd'
import { EyeOutlined, CheckCircleOutlined, PrinterOutlined } from '@ant-design/icons'
import { SelectC, RangePickerC, InputC, Search } from '@/i2antdV5'
import { renderTextStr, renderPhoneStr, formatDayString } from '@/utils/renderUtils'
import { PACKAGE_TYPE_OPTIONS, NOW_STATUS_OPTIONS } from '../config/orderTypeConfig'
import { fetchOrdersByType, ORDER_LIST_REDUX_SCOPE } from '@/redux/order/maternityCenterOrderSlice'
import HandleAbnormalOrderModal from '../share/HandleAbnormalOrderModal'
import OrderDetailDrawer from '../share/OrderDetailDrawer'
import BatchOrderPrintModal from '../print/BatchOrderPrintModal'

/** 与 AppHeader / 派单页一致 */
const MOBILE_LAYOUT_MAX_WIDTH = 768

class MaternityCenterOrderTable extends React.Component {
  constructor(props) {
    super(props)
    this.toolbarRef = React.createRef()
    this.mobileLayoutMqRef = null
    this.handleMobileLayoutMqChange = this.handleMobileLayoutMqChange.bind(this)
    this.state = {
      tableHeight: 600, // 默认高度
      selectedRowKeys: [],
      batchPrintVisible: false,
      isMobileLayout:
        typeof window !== 'undefined' &&
        window.matchMedia(`(max-width: ${MOBILE_LAYOUT_MAX_WIDTH}px)`).matches
    }
  }

  componentDidMount() {
    const { orderTypeData, jStoreOptions = [] } = this.props

    this.mobileLayoutMqRef = window.matchMedia(`(max-width: ${MOBILE_LAYOUT_MAX_WIDTH}px)`)
    this.mobileLayoutMqRef.addEventListener('change', this.handleMobileLayoutMqChange)

    // 延迟计算表格高度，确保 DOM 渲染完成
    setTimeout(() => {
      this.calculateTableHeight()
    }, 100)

    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize)

    // 先确保门店列表已加载，再用第一个门店 id 初始化订单列表请求
    // （避免出现默认门店 id 写死/先拉订单再拉门店的问题）
    this.tryInitFetchOrders({ orderTypeData, jStoreOptions })
  }

  componentDidUpdate(prevProps) {
    const prevOptionsLen = prevProps.jStoreOptions?.length || 0
    const nextOptionsLen = this.props.jStoreOptions?.length || 0

    // 门店列表从无到有：初始化拉取一次订单
    if (prevOptionsLen === 0 && nextOptionsLen > 0) {
      this.tryInitFetchOrders({
        orderTypeData: this.props.orderTypeData,
        jStoreOptions: this.props.jStoreOptions
      })
    }

    // 当筛选条件变化可能导致工具栏高度变化时，重新计算表格高度
    if (prevProps.orderTypeData?.query !== this.props.orderTypeData?.query) {
      setTimeout(() => {
        this.calculateTableHeight()
      }, 100)
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
    if (this.mobileLayoutMqRef) {
      this.mobileLayoutMqRef.removeEventListener('change', this.handleMobileLayoutMqChange)
    }
  }

  handleMobileLayoutMqChange() {
    if (!this.mobileLayoutMqRef) return
    const next = this.mobileLayoutMqRef.matches
    this.setState({ isMobileLayout: next }, () => {
      if (!next) {
        setTimeout(() => this.calculateTableHeight(), 100)
      }
    })
  }

  /**
   * 计算表格高度
   */
  calculateTableHeight = () => {
    if (this.state.isMobileLayout) {
      return
    }

    const viewportHeight = window.innerHeight

    // 头部高度（PageHeader）
    const headerHeight = 88
    
    // 动态获取工具栏实际高度
    let toolbarHeight = 60 // 默认高度
    if (this.toolbarRef.current) {
      const toolbarElement = this.toolbarRef.current
      const toolbarRect = toolbarElement.getBoundingClientRect()
      toolbarHeight = toolbarRect.height || 60
    }
    
    // Card 的 padding（上下各 16px）
    const cardPadding = 32
    // 分页器高度
    const paginationHeight = 56
    // Tabs 高度
    const tabsHeight = 46
    // Card margin 和其他间距
    const cardMargin = 16
    // 可配置的额外高度，用于增加表格高度（正数增加高度，负数减少高度）
    // 例如：设置为 50 会让表格高度增加 50px
    const extraHeight = 100
    
    // 计算表格可用高度
    const availableHeight = viewportHeight 
      - headerHeight 
      - toolbarHeight 
      - cardPadding 
      - paginationHeight 
      - tabsHeight
      - cardMargin
    
    // 设置最小高度，不设置最大高度限制
    const minHeight = 300
    const baseTableHeight = Math.max(minHeight, availableHeight)
    
    // 加上额外高度调整（正数增加高度，负数减少高度）
    const tableHeight = baseTableHeight + extraHeight
    
    this.setState({ tableHeight })
  }

  /**
   * 处理窗口大小变化
   */
  handleResize = () => {
    this.calculateTableHeight()
  }

  /**
   * 初始化拉取订单：门店列表拿到后，用（URL/已有query）或第一个门店 id 拉取
   */
  tryInitFetchOrders = ({ orderTypeData, jStoreOptions }) => {
    // 必须先有门店列表，才能确定默认 storeId
    if (!Array.isArray(jStoreOptions) || jStoreOptions.length === 0) return

    const isFetching = orderTypeData?.fetching || false
    const existingQuery = orderTypeData?.query || {}

    const storeIdToUse =
      (existingQuery && existingQuery.storeId) ||
      (jStoreOptions?.[0]?.id != null ? String(jStoreOptions[0].id) : '')

    if (!storeIdToUse) return

    const initQuery = {
      ...existingQuery,
      storeId: String(storeIdToUse),
      pageNumber: existingQuery.pageNumber || 1,
      pageSize: existingQuery.pageSize || 20
    }

    // 与派单页分库存储 orderListPage / dispatchOrderList；进入本页时未在请求中则按 initQuery 拉一次。
    if (!isFetching) {
      this.fetchOrders(initQuery)
    }
  }

  fetchOrders = (customQuery) => {
    const { dispatch } = this.props
    
    const orderType = 'maternity_center'
    dispatch(
      fetchOrdersByType({
        query: customQuery,
        orderType,
        orderListScope: ORDER_LIST_REDUX_SCOPE.ORDER_LIST_PAGE
      })
    )
  }

  handlePageChange = (pageNumber, pageSize) => {
    const { dispatch, orderTypeData: { query } } = this.props
    this.setState({ selectedRowKeys: [] })
    this.fetchOrders({
      ...query,
      pageNumber,
      pageSize
    })
  }

  handleBatchPrint = () => {
    this.setState({ batchPrintVisible: true })
  }

  handleBatchPrintClose = () => {
    this.setState({ batchPrintVisible: false, selectedRowKeys: [] })
  }

  /**
   * 计算表格列的总宽度
   */
  getTableScrollX = () => {
    const columns = this.getColumns()
    const totalWidth = columns.reduce((sum, col) => {
      return sum + (col.width || 0)
    }, 0)
    return totalWidth
  }

  getColumns = () => {
    const { isMobileLayout } = this.state
    const cols = [
      // 核心标识
      {
        title: '订单号',
        dataIndex: 'id',
        key: 'id',
        width: 80,
        fixed: 'left',
        render: (text) => renderTextStr(text)
      },
      {
        title: '订单状态',
        dataIndex: 'orderStatus',
        key: 'orderStatus',
        width: 120,
        render: (text) => renderTextStr(text)
      },
      {
        title: '客户信息',
        key: 'customerInfo',
        width: 180,
        render: (_, record) => {
          const name = renderTextStr(record.customerName)
          const phone = renderPhoneStr(record.customerPhone)
          return (
            <div>
              <div>{name}</div>
              <div style={{ color: '#999', fontSize: '12px' }}>{phone}</div>
            </div>
          )
        }
      },
      {
        title: '服务时间',
        key: 'serviceTime',
        width: 200,
        render: (_, record) => {
          const startTimeRaw = (record.momCheckInTime && record.momCheckInTime !== 0)
            ? record.momCheckInTime
            : (record.momAppointInTime)
          return (
            <div>
              <div>开始：{formatDayString(startTimeRaw)}</div>
              <div style={{ color: '#999', fontSize: '12px' }}>结束：{formatDayString(record.leaveTime)}</div>
            </div>
          )
        }
      },
      // 服务与门店
      {
        title: '门店',
        dataIndex: 'storeName',
        key: 'storeName',
        width: 180,
        render: (text) => renderTextStr(text)
      },
      {
        title: '套餐名称',
        dataIndex: 'packageName',
        key: 'packageName',
        width: 200,
        render: (text) => renderTextStr(text)
      },
      {
        title: '房间号',
        dataIndex: 'roomTypeName',
        key: 'roomTypeName',
        width: 100,
        render: (text) => renderTextStr(text)
      },
      {
        title: '服务天数',
        dataIndex: 'serviceDay',
        key: 'serviceDay',
        width: 120,
        render: (text) => renderTextStr(text)
      },
      // 人员与金额
      {
        title: '接单人员',
        dataIndex: 'signUserName',
        key: 'signUserName',
        width: 120,
        render: (text) => renderTextStr(text)
      },
      {
        title: '订单金额',
        dataIndex: 'orderAmount',
        key: 'orderAmount',
        width: 120,
        align: 'right',
        render: (amount) => amount ? `¥${Number(amount).toFixed(2)}` : '-'
      },
      {
        title: '创建/签单时间',
        key: 'createSignTime',
        width: 200,
        render: (_, record) => (
          <div>
            <div>创建：{formatDayString(record.createTime)}</div>
            <div style={{ color: '#999', fontSize: '12px' }}>签单：{formatDayString(record.signTime)}</div>
          </div>
        )
      },
      {
        title: '操作',
        key: 'action',
        width: 100,
        fixed: 'right',
        render: (_, record) => {
          return (
            <Space size="small">
              <OrderDetailDrawer
                baseProps={{
                  orderId: record.id,
                  orderType: 'maternity_center',
                }}
              >
                <Button
                  type="link"
                  size="small"
                  icon={<EyeOutlined />}
                >
                  详情
                </Button>
              </OrderDetailDrawer>
              {record.isAbnormal && (
                <HandleAbnormalOrderModal
                  baseProps={{
                    orderId: record.id,
                    orderDetail: record,
                  }}
                >
                  <Button
                    type="link"
                    size="small"
                    danger
                    icon={<CheckCircleOutlined />}
                  >
                    处理
                  </Button>
                </HandleAbnormalOrderModal>
              )}
            </Space>
          )
        }
      }
    ]

    if (isMobileLayout) {
      return cols.map((col) => {
        if (!col.fixed) return col
        const { fixed, ...rest } = col
        return rest
      })
    }
    return cols
  }

  render() {
    const { jStoreOptions, orderTypeData } = this.props
    const { isMobileLayout } = this.state

    // 从 Redux state 中获取数据
    const { fetching, data = {}, query } = orderTypeData
    const { records = [], totalRow = 0, pageNumber = 1, pageSize = 20 } = data

    const columns = this.getColumns()

    const scrollX = this.getTableScrollX()
    const scroll = { x: scrollX }
    if (!isMobileLayout) {
      scroll.y = this.state.tableHeight || 600
    }

    const toolbarClassName = [
      'i2-app-toolbar',
      'maternity-center-order-toolbar',
      isMobileLayout ? 'maternity-center-order-toolbar--mobile' : ''
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <>
        <section className={toolbarClassName} ref={this.toolbarRef}>
          <div className="toolbar-filters">
            <Search
              label="关键词"
              query={query}
              searchKey="keyword"
              action={this.fetchOrders}
              placeholder="订单号、合同号、客户姓名、手机号"
              modalProps={{
                allowClear: true,
                style: isMobileLayout ? { width: '100%', maxWidth: 'none' } : { width: 240 }
              }}
            />
            <SelectC
              label="门店"
              query={query}
              searchKey="storeId"
              options={jStoreOptions}
              action={this.fetchOrders}
              placeholder="请选择门店"
              modalProps={{
                allowClear: false
              }}
            />
            <SelectC
              label="入住状态"
              query={query}
              searchKey="checkInStatus"
              options={PACKAGE_TYPE_OPTIONS}
              action={this.fetchOrders}
              placeholder="入住状态"
            />
            <SelectC
              label="当前状态"
              query={query}
              searchKey="nowState"
              options={NOW_STATUS_OPTIONS}
              action={this.fetchOrders}
              placeholder="当前状态"
            />
            <RangePickerC
              label="订单日期"
              query={query}
              searchKey={['orderDateStart', 'orderDateEnd']}
              action={this.fetchOrders}
              placeholder={['开始日期', '结束日期']}
            />
            <RangePickerC
              label="入住日期"
              query={query}
              searchKey={['checkInDateStart', 'checkInDateEnd']}
              action={this.fetchOrders}
              placeholder={['开始日期', '结束日期']}
            />
            <RangePickerC
              label="离院日期"
              query={query}
              searchKey={['leaveDateStart', 'leaveDateEnd']}
              action={this.fetchOrders}
              placeholder={['开始日期', '结束日期']}
            />
          </div>
          <div className="toolbar-actions">
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              disabled={!this.state.selectedRowKeys.length}
              onClick={this.handleBatchPrint}
            >
              批量打印 {this.state.selectedRowKeys.length ? `(${this.state.selectedRowKeys.length})` : ''}
            </Button>
          </div>
        </section>
        <div className={isMobileLayout ? 'maternity-center-order-table-wrap' : undefined}>
        <Table
          loading={fetching}
          columns={columns}
          dataSource={records}
          rowKey="id"
          rowSelection={{
            selectedRowKeys: this.state.selectedRowKeys,
            onChange: (keys) => this.setState({ selectedRowKeys: keys })
          }}
          scroll={scroll}
          size="small"
          pagination={{
            current: pageNumber,
            pageSize: pageSize,
            total: totalRow,
            showSizeChanger: !isMobileLayout,
            showTotal: (t) => (isMobileLayout ? `共${t}条` : `共 ${t} 条`),
            size: isMobileLayout ? 'small' : 'default',
            onChange: this.handlePageChange,
            onShowSizeChange: (current, size) => this.handlePageChange(1, size)
          }}
        />
        </div>
        <BatchOrderPrintModal
          visible={this.state.batchPrintVisible}
          orderIds={this.state.selectedRowKeys}
          onClose={this.handleBatchPrintClose}
        />
      </>
    )
  }
}

/**
 * Wrapper 组件：连接 Redux，获取数据并传递给类组件
 */
const MaternityCenterOrderTableWrapper = (props) => {
  const dispatch = useDispatch()
  
  // 从 Redux state 中获取订单数据
  const orderState = useSelector(state => state.order?.order || {})
  const orderTypeData = orderState.orderListPage || {}
  
  // 从 Redux state 中获取门店列表
  const componentsState = useSelector(state => state.components || {})
  const jStoreListObj = componentsState.getJStoreList || {}
  const jStoreOptions = jStoreListObj.data || []
  
  return (
    <MaternityCenterOrderTable
      {...props}
      dispatch={dispatch}
      orderTypeData={orderTypeData}
      jStoreOptions={jStoreOptions}
    />
  )
}

export default MaternityCenterOrderTableWrapper
